import { Anchor, Box, Button, CopyButton, Group, Popover, Stack, Text, Tooltip, UnstyledButton, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconCopy, IconPhone, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { axioTokens } from "../../theme";
import { toTelUri } from "../../lib/phone";
import {
  CALL_CANDIDATE_LABEL,
  CALL_COARSE_POINTER_QUERY,
  CALL_COPY_NUMBER_LABEL,
  CALL_DIAL_POPOVER_QR_SIZE,
  CALL_DIAL_QR_SIZE,
  CALL_NUMBER_COPIED,
  CALL_ON_THIS_PHONE_LABEL,
  CALL_POPOVER_CANCEL_LABEL,
  CALL_SCAN_TO_DIAL_COPY,
  POPOVER_CLOSE_STYLE,
} from "./constants";

export function CallDialQr({
  phone,
  qrSize = CALL_DIAL_QR_SIZE,
  forceQr = false,
  fill = false,
}: {
  phone: string;
  qrSize?: number;
  forceQr?: boolean;
  fill?: boolean;
}) {
  const theme = useMantineTheme();
  const telUri = toTelUri(phone);
  const isNarrow = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`, false, {
    getInitialValueInEffect: false,
  });
  const isCoarsePointer = useMediaQuery(CALL_COARSE_POINTER_QUERY, false, {
    getInitialValueInEffect: false,
  });

  if (!telUri) return null;

  if (!forceQr && (isNarrow || isCoarsePointer)) {
    return (
      <Button
        component="a"
        href={telUri}
        size="compact-sm"
        color="violet"
        leftSection={<IconPhone size="var(--axio-unit-16)" />}
      >
        {CALL_ON_THIS_PHONE_LABEL}
      </Button>
    );
  }

  const qr = (
    <QRCodeSVG
      value={telUri}
      size={qrSize}
      level="M"
      includeMargin={!fill}
      title={CALL_SCAN_TO_DIAL_COPY}
      style={
        fill
          ? {
              width: axioTokens.percentage.full,
              height: axioTokens.percentage.full,
              display: "block",
            }
          : undefined
      }
    />
  );

  if (fill) {
    return (
      <Stack
        gap="var(--axio-unit-6)"
        align="center"
        justify="space-between"
        h={axioTokens.percentage.full}
        style={{
          alignSelf: "stretch",
          aspectRatio: "1 / 1",
          flexShrink: axioTokens.number.zero,
        }}
      >
        <Box
          style={{
            flex: axioTokens.number.one,
            minWidth: axioTokens.unit[0],
            minHeight: axioTokens.unit[0],
            width: axioTokens.percentage.full,
          }}
        >
          {qr}
        </Box>
        <Text size="xs" c="dimmed" ta="center">
          {CALL_SCAN_TO_DIAL_COPY}
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="xs" align="center">
      {qr}
      <Text size="xs" c="dimmed" ta="center">
        {CALL_SCAN_TO_DIAL_COPY}
      </Text>
    </Stack>
  );
}

export function PhoneDialLinks({ phone }: { phone: string }) {
  const [callOpen, setCallOpen] = useState(false);

  if (!toTelUri(phone)) return null;

  return (
    <Group gap="var(--axio-unit-6)" wrap="nowrap">
      <CopyButton value={phone}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? CALL_NUMBER_COPIED : CALL_COPY_NUMBER_LABEL}>
            <Anchor
              component="button"
              type="button"
              size="xs"
              c="dimmed"
              aria-label={CALL_COPY_NUMBER_LABEL}
              display="inline-flex"
              onClick={copy}
            >
              <IconCopy size="var(--axio-unit-14)" />
            </Anchor>
          </Tooltip>
        )}
      </CopyButton>
      <Popover opened={callOpen} onChange={setCallOpen} position="bottom-start" withinPortal>
        <Popover.Target>
          <Tooltip label={CALL_CANDIDATE_LABEL}>
            <UnstyledButton
              type="button"
              aria-label={CALL_CANDIDATE_LABEL}
              c="violet"
              display="inline-flex"
              style={{ lineHeight: axioTokens.lineHeight.solid }}
              onClick={(event) => {
                event.stopPropagation();
                setCallOpen((open) => !open);
              }}
            >
              <IconPhone size="var(--axio-unit-14)" />
            </UnstyledButton>
          </Tooltip>
        </Popover.Target>
        <Popover.Dropdown p="sm">
          <Box pos="relative">
            <Anchor
              href="#"
              c="dimmed"
              display="inline-flex"
              underline="never"
              aria-label={CALL_POPOVER_CANCEL_LABEL}
              style={POPOVER_CLOSE_STYLE}
              onClick={(event) => {
                event.preventDefault();
                setCallOpen(false);
              }}
            >
              <IconX size="var(--axio-unit-14)" />
            </Anchor>
            <CallDialQr phone={phone} qrSize={CALL_DIAL_POPOVER_QR_SIZE} forceQr />
          </Box>
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
}
